import { useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Box, Grid, Typography, Avatar } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useIsFetching, useIsMutating, useMutation } from "@tanstack/react-query";
import { AiOutlineUser, AiOutlineHome, AiOutlineMail } from "react-icons/ai";
import { MdArrowForwardIos } from "react-icons/md";
import { BiSearch, BiExit } from "react-icons/bi";
import { TbMessageCircle2 } from "react-icons/tb";
import { FiUsers } from "react-icons/fi";
import { useAuth } from "../hooks";
import { setBackIcon, setDashboardMessages, setUserCurrentState } from "../slice";
import Status from "./Status";
import Navlink from "./Navlink";
import MenuDrawer from "./MenuDrawer";
import { PALETTE } from "../Theme/Palette";
import DashboardMessageCard from "./Messages/DashboardMessageCard";
import Loading from "./Loading";
import { ReactComponent as Logo1White } from "../assets/images/Logo_1_white.svg";
import { ReactComponent as Logo1Horizontal } from "../assets/images/Logo_1_horizontal.svg";
import ConfirmModal from "./ConfirmModal";
import { USERS_API } from "../api";
import { useCookies } from "react-cookie";
import { ACCESS_KEY, convertCookies, REFRESH_KEY } from "../services/auth";

function Layout({ children }) {
  const container = useRef();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isFetching = useIsFetching();
  const isRefreshingToken = useIsFetching({ queryKey: ["refreshToken"] });
  const isMutating = useIsMutating();
  const { signOut } = useAuth();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const { data, isNewUser, userInfo } = useSelector((state) => state.UserCurrentState);
  const { title, hasBackIcon, route, hasTitleRoute } = useSelector((state) => state.PageTitle);
  const { isLoading } = useSelector((state) => state.Loading);
  const [cookies] = useCookies(ACCESS_KEY);
  const refresh_token = convertCookies(cookies[REFRESH_KEY]);

  const { mutate: logOut } = useMutation({
    mutationFn: () => USERS_API.LogOut(refresh_token),
    onSuccess: async () => {
      await dispatch(setDashboardMessages([]));
      await window.sessionStorage.setItem("isAnimationShown", true);
      await dispatch(setUserCurrentState({}));
      await signOut();
    },
  });

  const handleLogOut = () => {
    window.sessionStorage.clear();
    logOut();
  };

  const handleSearchAndCandidateAccess = () => {
    let accessCodes = [65, 70, 8, 50];
    return !accessCodes.includes(parseInt(data?.code));
  };

  useLayoutEffect(() => {
    if (container.current) {
      container.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [location.pathname]);

  return (
    <Grid
      container
      height="100vh"
      maxWidth="1920px"
      sx={{ backgroundColor: "#FAFAFA" }}
      display={{ sx: "none", md: "flex" }}
    >
      {((isFetching !== 0 && !isRefreshingToken) || isMutating !== 0 || isLoading) && <Loading />}
      <ConfirmModal
        message="آیا از اینکه می خواهید خارج شوید مطمئن هستید؟"
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        onSubmit={handleLogOut}
      />
      <Grid
        ref={container}
        width={{ xs: "100%", sm: "100%", md: "calc(100% - 250px)" }}
        height="100%"
        sx={{
          overflowY: "auto",
          direction: "rtl",
          background: "#EEEEEE",
          position: "relative",
        }}
        flexDirection="column"
        p={{ xs: 2, sm: 3, md: 3 }}
        pt={{ xs: 3 }}
        id="scrollable_div"
      >
        <Grid
          item
          xs={12}
          display={{ sm: "flex", xs: "flex", md: "none" }}
          justifyContent="space-between"
          sx={{ direction: "ltr" }}
          mb={2}
        >
          <Grid item xs={2}>
            <MenuDrawer onOpenConfirmModal={(open) => setOpenConfirmModal(open)} />
          </Grid>
          <Grid item xs={4} display="flex" justifyContent="flex-end">
            <Logo1Horizontal />
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          justifyContent="space-between"
          alignItems="center"
          bgcolor="transparent"
          flexDirection="row-reverse"
          display="flex"
          mb={2}
          sx={{ direction: "ltr" }}
        >
          <Grid display="flex" justifyContent="center" alignItems="center">
            <Status />
            <Typography variant="h4" textAlign="right" fontFamily="VazirMedium" ml={1}>
              {isNewUser ? "کاربر" : userInfo.firstName} عزیز خوش آمدید
            </Typography>
            <Avatar style={{ widt: "40px", height: "40px", marginLeft: "10px" }} />
          </Grid>
          {!isNewUser && (
            <Typography fontSize="0.85rem" color="#4C466F" sx={{ direction: "rtl" }}>
              عضو:
              <Link
                to={`center-profile/${userInfo.branchId}`}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {userInfo.branchName}
              </Link>
            </Typography>
          )}
        </Grid>
        <Grid
          item
          minHeight="70vh"
          xs={12}
          bgcolor="#fff"
          borderRadius={4}
          sx={{ p: { xs: 3, md: 4 }, direction: "ltr" }}
        >
          <Grid container display="flex" flexDirection="row-reverse" alignItems="flex-start">
            {hasBackIcon && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="1.3rem"
                height="1.3rem"
                borderRadius="50%"
                mb="0.3rem"
                ml={2}
                pl="0.1rem"
                boxSizing="border-box"
                border="1px solid #4C466F"
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => navigate(route)}
              >
                <MdArrowForwardIos size="0.7rem" color="#4C466F" />
              </Box>
            )}
            {title.length > 0 && (
              <Typography
                display="inline"
                variant="h1"
                fontWeight={600}
                textAlign="right"
                mb={2}
                fontSize="22px"
                sx={hasTitleRoute && { cursor: "pointer" }}
                onClick={() => {
                  if (hasTitleRoute) navigate(route);
                }}
              >
                {title}
              </Typography>
            )}
          </Grid>

          <DashboardMessageCard />
          {children}
        </Grid>
      </Grid>
      <Grid
        item
        width="250px"
        height="100%"
        flexDirection="column"
        justifyContent="center"
        sx={{
          background:
            parseInt(userInfo.genderTypeEnum) === 1 ? PALETTE.maleGradient : PALETTE.femaleGradient,
          position: "sticky",
          overflowY: "auto",
          top: "0",
        }}
        display={{ sm: "none", xs: "none", md: "block" }}
      >
        <Grid
          display="flex"
          justifyContent="center"
          alignItems="center"
          paddingTop="4rem"
          paddingBottom="3rem"
        >
          <Logo1White />
        </Grid>
        <Box px={{ md: 0.5, lg: 1 }}>
          <Navlink name="صفحه اول" href="/home" activationUrl="home" Icon={AiOutlineHome} />
          <Navlink
            name="اطلاعات من"
            href="/personal-info/base-info"
            activationUrl="personal-info"
            Icon={AiOutlineUser}
          />
          {/* <Navlink
            name="تست های روانشناسی"
            href="/tests"
            activationUrl="tests"
            icon={BiNotepad}
          /> */}
          <Navlink
            name="پیام ها"
            href="/messages"
            activationUrl="messages"
            Icon={TbMessageCircle2}
          />
          <Navlink
            name="پیگیری ها و پاسخگویی"
            href="/ticketing"
            activationUrl="ticketing"
            Icon={AiOutlineMail}
          />
          {/* <Navlink
            name="آموزش و مشاوره"
            href="/consulting"
            activationUrl="consulting"
            icon={FaGraduationCap}
          /> */}
          {/* <Navlink
            name="سوالات متداول"
            href="/questions"
            activationUrl="questions"
            icon={TbBook}
          /> */}
          <Navlink
            name="جستجو و کاندیدها"
            href="/search-candidates"
            activationUrl="search-candidates"
            Icon={BiSearch}
            disabled={handleSearchAndCandidateAccess()}
          />
          <Navlink name="اصلاح پروفایل" href="/profile" activationUrl="profile" Icon={FiUsers} />
          {/* <Navlink
            name="درخواست مشاوره "
            href="consultation-request"
            activationUrl="consultation-request"
            icon={BiPhone}
          /> */}
          <Navlink
            name="خروج"
            href="/log-out"
            activationUrl="log-out"
            Icon={BiExit}
            onClick={() => {
              dispatch(setBackIcon(false));
              setOpenConfirmModal(true);
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}

export default Layout;
